import React, { useState, useEffect, useContext } from 'react';
import { PDFDocument, rgb, degrees } from 'pdf-lib';
import DashboardLayout from '../common/DashboardLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import AuthContext from '../../context/AuthContext';
import httpServices from '../../services/httpServices';

// React-PDF setup
import { Document, Page, pdfjs } from 'react-pdf';
// Set worker locally to avoid CDN/CORS issues
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const CustomTrainingPDFViewer = () => {
  const [pdfSrc, setPdfSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [expiredate, setExpiredate] = useState(null);
  const [courseTitle, setCourseTitle] = useState('');
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { userrole } = useContext(AuthContext);

  // PDF Rendering State
  const [numPages, setNumPages] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');

  function onDocumentLoadSuccess({ numPages }) {
    console.log('[DEBUG] PDF Loaded Successfully. Pages:', numPages);
    setNumPages(numPages);
  }

  function onDocumentLoadError(error) {
    console.error('[DEBUG] PDF Load Failed:', error);
    setError(error.message);
  }

  const handleTakeQuiz = () => {
    navigate(`/custom-training/quiz/${courseId}`);
  };

  const addWatermark = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch PDF from backend proxy with authentication
      // Extract the path from the full URL
      const urlPath = pdfUrl.replace(process.env.REACT_APP_API_BASE_URL, '');

      // Use axios directly with responseType arraybuffer for PDF
      const response = await httpServices.getBlob(urlPath);

      if (!response || !response.data) {
        throw new Error('Failed to fetch PDF');
      }

      // Convert blob to arrayBuffer
      const existingPdfBytes = await response.data.arrayBuffer();

      // Check if we got HTML back (SPA fallback) instead of PDF
      const firstBytes = new Uint8Array(existingPdfBytes.slice(0, 5));
      const firstString = new TextDecoder().decode(firstBytes);
      if (firstString.includes('<') || firstString.includes('<!')) {
        throw new Error('Server returned HTML instead of PDF. File may be missing.');
      }

      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const { width, height } = pages[0].getSize();
      const watermarkText = `Expires ${expiredate}`;

      pages.forEach((page) => {
        page.drawText(watermarkText, {
          x: width / 4,
          y: height / 3,
          size: 60,
          color: rgb(0.7, 0.7, 0.7),
          rotate: degrees(45),
          opacity: 0.5,
        });
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });

      if (pdfSrc) {
        URL.revokeObjectURL(pdfSrc);
      }

      setPdfSrc(URL.createObjectURL(blob));

      // Mark course as in progress when PDF is successfully loaded
      try {
        await httpServices.post(`/admin/custom-training/courses/${courseId}/mark-in-progress`);
        console.log('Course marked as in progress');
      } catch (error) {
        console.error('Error marking course in progress:', error);
        // Don't show error to user - this is a background operation
      }
    } catch (err) {
      console.error('Error adding watermark:', err);
      const errorMessage = err.message || 'Failed to process PDF';
      setError(`Failed to load PDF: ${errorMessage}. Please ensure the file exists at: ${pdfUrl}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pdfUrl && expiredate) {
      addWatermark();
    }
  }, [expiredate, pdfUrl]);

  useEffect(() => {
    const fetchPDFInfo = async () => {
      try {
        const response = await httpServices.get(`/admin/custom-training/courses/${courseId}/pdf-info`);

        if (!response) {
          throw new Error('PDF information not found for this course.');
        }

        // Use backend proxy URL instead of direct S3 URL to avoid CORS
        const backendPdfUrl = `${process.env.REACT_APP_API_BASE_URL}admin/custom-training/courses/${courseId}/pdf`;
        setPdfUrl(backendPdfUrl);
        setCourseTitle(response.courseTitle || 'Custom Training Course');

        const expirationDate = new Date(response.expirationDate);
        const formattedDate = format(expirationDate, 'MM dd yyyy');
        setExpiredate(formattedDate);
        console.log(formattedDate, 'expiration date');

      } catch (error) {
        console.error('Error fetching PDF info:', error);
        // A 403 means the course is not published to this user's school, which
        // is a completely different problem from a missing file. Saying "make
        // sure the PDF exists" sent people hunting through S3 for a file that
        // was there all along.
        const status = error?.response?.status;
        if (status === 403) {
          setError('This course is not available to your school. If you think that is wrong, contact your administrator.');
        } else if (status === 401) {
          setError('Your session has expired. Please sign in again.');
        } else if (status === 404) {
          // The backend distinguishes a missing course from a course with no
          // stored document; both are 404, so read the message rather than
          // telling someone their course was deleted when it is sitting there.
          const detail = error?.response?.data?.error || '';
          setError(
            /no pdf available/i.test(detail)
              ? 'This course has no training document attached. If it was built from a PDF, open it with Launch Course instead.'
              : 'This course no longer exists.'
          );
        } else {
          setError('Could not retrieve course PDF. Please try again.');
        }
        setNotFound(status === 404 || status === undefined);
        setLoading(false);
      }
    };

    fetchPDFInfo();
  }, [courseId]);

  // Get user type for proper role handling
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.type || null;

  // Robust Role Retrieval
  let rawRole = userrole;
  let roleSource = 'Context';

  if (!rawRole) {
    rawRole = localStorage.getItem('userrole');
    roleSource = 'LocalStorage(userrole)';
  }

  if (!rawRole && user?.role) {
    rawRole = user.role;
    roleSource = 'LocalStorage(user.role)';
  }

  const role = rawRole ? String(rawRole) : null;

  console.log('[CustomTrainingPDFViewer] Layout Params:', {
    userType,
    rawRole,
    role,
    roleSource,
    courseId,
    courseTitle,
    userDetails: user
  });

  return (
    <DashboardLayout
      role={role}
      userType={userType}
      layoutType="topnav"
      showBreadcrumbs={true}
    >
      <div className="container">
        <div className="my-4">
          <h2>{courseTitle}</h2>
          <p className="text-muted">Custom Training Material</p>
        </div>

        {loading && <p className="text-center py-4">Processing PDF and adding secure watermark...</p>}
        {error && (
          <div style={{ color: 'red', padding: '20px', background: '#ffe6e6', borderRadius: '5px', marginBottom: '20px' }}>
            <strong>Error:</strong> {error}
            {notFound && pdfUrl && (
              <>
                <br />
                <small>Make sure the PDF file exists at: {pdfUrl}</small>
              </>
            )}
          </div>
        )}

        {pdfSrc && !loading && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' }}>
              <button
                onClick={handleTakeQuiz}
                style={{
                  padding: '10px 20px',
                  fontSize: '16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  width: '200px',
                  fontWeight: '600'
                }}
              >
                📝 Take Quiz
              </button>
              <a href={pdfSrc} target="_blank" rel="noopener noreferrer">
                <button
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    width: '200px'
                  }}
                >
                  Open in New Tab
                </button>
              </a>
              <a href={pdfSrc} download={`${courseTitle}.pdf`}>
                <button
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    width: '200px'
                  }}
                >
                  Download PDF
                </button>
              </a>
            </div>

            <div className="d-flex justify-content-center bg-light p-4 border rounded" style={{ minHeight: '600px' }}>
              <Document
                file={pdfSrc}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-center">Loading PDF renderer...</div>}
                error={<div className="text-danger">Failed to render PDF inline. Please use the &quot;Open in New Tab&quot; button.</div>}
              >
                {Array.from(new Array(numPages || 0), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={1.2}
                    className="mb-4 shadow-sm"
                  />
                ))}
              </Document>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomTrainingPDFViewer;
